import React, { FC, useEffect } from 'react';
import { Box } from '@mui/material';
import { AuthSteps } from 'components/modals/AuthModal/Drawer';
import { Portal } from 'components/generic';
import { animated, useSpring } from 'react-spring';
import { useDrag } from '@use-gesture/react';
import { useWindowDimensions } from 'hooks/menu';
import { useDisableBodyScroll, useDelayUnmount } from 'hooks';
import { FormProvider } from 'react-hook-form';
import { Step } from 'components/modals/AuthModal';

interface DrawerProps {
  step: Step;
  methods: any;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: () => void;
  nextStep: () => void;
  prevStep: () => void;
}

const Drawer: FC<DrawerProps> = ({
  step,
  methods,
  isOpen,
  onClose,
  onSubmit,
  nextStep,
  prevStep,
}) => {
  useDisableBodyScroll(isOpen);
  const isMount = useDelayUnmount(isOpen, 500);
  const { pageHeight } = useWindowDimensions();
  const [{ y }, api] = useSpring(() => ({
    y: pageHeight,
    opacity: 0,
  }));
  const open = () => {
    api.start({
      y: 0,
      immediate: false,
    });
  };
  const close = () => {
    api.start({
      y: pageHeight,
      immediate: false,
    });
    if (isOpen) {
      onClose();
    }
  };

  useEffect(() => {
    if (isOpen) {
      open();
    } else {
      close();
    }
  }, [isOpen]);

  const bind = useDrag(
    ({
      last,
      velocity: [, vy],
      direction: [, dy],
      movement: [, my],
      cancel,
    }) => {
      if (my < 0) cancel();

      if (last) {
        if (my > pageHeight * 0.5 || (vy > 0.5 && dy > 0)) {
          close();
        } else {
          open();
        }
      } else api.start({ y: my, immediate: true });
    },
    {
      axis: 'y',
      from: () => [0, y.get()],
      filterTaps: true,
      bounds: { top: 0 },
      rubberband: true,
    }
  );

  return (
    <Portal>
      {isMount && (
        <Box>
          <animated.div
            {...bind()}
            style={{
              touchAction: 'none',
              position: 'fixed',
              top: '0px',
              left: '0px',
              right: '0px',
              width: '100%',
              height: '100svh',
              background: '#ffffff',
              y,
            }}
          >
            <Box component={FormProvider} {...methods}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                }}
                component="form"
                onSubmit={methods.handleSubmit(onSubmit)}
              >
                <AuthSteps
                  step={step}
                  onClose={onClose}
                  nextStep={nextStep}
                  prevStep={prevStep}
                />
              </Box>
            </Box>
          </animated.div>
        </Box>
      )}
    </Portal>
  );
};

export default Drawer;
